import { ReactNode } from 'react';

import { Color, cn } from 'styles';

import { AlertCircleIcon, CheckCircleIcon, ErrorCircleIcon, WarningIcon } from '../Icons';
import { Column } from '../Containers';
import { StringNodeGuard } from '../helpers/OrStringNode';

type AlertLevel =
  | 'custom'
  | Extract<Color, 'info' | 'warning' | 'success' | 'destructive' | 'primary' | 'secondary'>;

type AlertType = 'filled' | 'outlined' | 'lightly-filled';

const levelClasses: { [Key in AlertLevel]: { [T in AlertType]: string } } = {
  info: {
    filled: 'bg-info text-info-foreground border-transparent',
    'lightly-filled': 'bg-info/20 text-info-dark border-transparent',
    outlined: 'border-info text-info',
  },
  warning: {
    filled: 'bg-warning text-warning-foreground border-transparent',
    'lightly-filled': 'bg-warning/20 text-warning-dark border-transparent',
    outlined: 'border-warning text-warning',
  },
  success: {
    filled: 'bg-success text-success-foreground border-transparent',
    'lightly-filled': 'bg-success/20 text-success-dark border-transparent',
    outlined: 'border-success text-success',
  },
  destructive: {
    filled: 'bg-destructive text-destructive-foreground border-transparent',
    'lightly-filled': 'bg-destructive/20 text-destructive-dark border-transparent',
    outlined: 'border-destructive text-destructive',
  },
  primary: {
    filled: 'bg-primary text-primary-foreground border-transparent',
    'lightly-filled': 'bg-primary/20 text-primary-dark border-transparent',
    outlined: 'border-primary text-primary',
  },
  secondary: {
    filled: 'bg-secondary text-secondary-foreground border-transparent',
    'lightly-filled': 'bg-secondary/20 text-secondary-dark border-transparent',
    outlined: 'border-secondary text-secondary',
  },
  custom: {
    filled: 'bg-inherit border-transparent',
    'lightly-filled': ' border-transparent',
    outlined: 'border-inherit text-inherit',
  },
};

type AlertProps = {
  type?: AlertType;
  level?: AlertLevel;
  title?: ReactNode;
  icon?: ReactNode;
  iconSize?: number;
  children?: ReactNode;

  className?: string;
  titleClassName?: string;
  bodyClassName?: string;
};

const LevelToIcon: Record<AlertLevel, typeof AlertCircleIcon> = {
  destructive: ErrorCircleIcon,
  warning: WarningIcon,
  info: AlertCircleIcon,
  success: CheckCircleIcon,
  custom: AlertCircleIcon,
  primary: AlertCircleIcon,
  secondary: AlertCircleIcon,
};

export function Alert({
  level = 'info',
  type = 'outlined',
  title,
  children,
  className,
  bodyClassName,
  titleClassName,
  icon,
  iconSize = 18,
}: AlertProps) {
  const DefaultIcon = LevelToIcon[level];

  return (
    <div
      role="alert"
      className={cn(
        'relative flex w-full gap-2 rounded-lg border p-4',

        levelClasses[level][type],

        title && children ? '[&>svg]:translate-y-[4px]' : 'items-center',

        className,
      )}
    >
      {icon ?? <DefaultIcon size={iconSize} />}

      <Column gap="0">
        <StringNodeGuard node={title}>
          <h5
            className={cn(
              'text-lg font-semibold leading-snug tracking-tight text-inherit',
              children && 'mb-1',
              titleClassName,
            )}
          >
            {title}
          </h5>
        </StringNodeGuard>

        <div className={cn('text-sm font-normal [&_p]:leading-relaxed', bodyClassName)}>
          {children}
        </div>
      </Column>
    </div>
  );
}
